/**
 * The application shell: masthead, toolbar, staff, and the four sheets that
 * open over it.
 *
 * The main screen is deliberately sparse — a row of verbs, the score, and two
 * corner controls. Everything that is a preference rather than an action lives
 * behind the spanner.
 */

import { useCallback, useEffect, useState, type JSX } from 'react';
import { useSession } from './state/store';
import { usePlayback } from './state/playback';
import { audio } from './audio/engine';
import { edoOf } from './core';
import { Masthead } from './ui/Masthead';
import { Toolbar } from './ui/Toolbar';
import { ScoreView } from './ui/score/ScoreView';
import { StatusStrip } from './ui/StatusStrip';
import { IconButton } from './ui/IconButton';
import { GradeArrowIcon, HelpIcon, SettingsIcon } from './ui/Icons';
import { SettingsDialog } from './ui/SettingsDialog';
import { HelpDialog } from './ui/HelpDialog';
import { KeyDialog } from './ui/KeyDialog';
import { GradeDialog } from './ui/GradeDialog';
import { ConfirmDialog, type ConfirmRequest } from './ui/ConfirmDialog';

type Sheet = 'none' | 'settings' | 'help' | 'key' | 'grade';

export function App(): JSX.Element {
  const settings = useSession((s) => s.settings);
  const exercise = useSession((s) => s.exercise);
  const newExercise = useSession((s) => s.newExercise);
  const gradeNow = useSession((s) => s.gradeNow);
  const undo = useSession((s) => s.undo);
  const redo = useSession((s) => s.redo);
  const isDirty = useSession((s) => s.isDirty);
  const report = useSession((s) => s.report);

  const togglePlay = usePlayback((s) => s.toggle);
  const tap = usePlayback((s) => s.tap);

  const [sheet, setSheet] = useState<Sheet>('none');
  const [confirm, setConfirm] = useState<ConfirmRequest | null>(null);

  /* ---- theme ------------------------------------------------------------ */
  useEffect(() => {
    const root = document.documentElement;
    if (settings.theme === 'system') root.removeAttribute('data-theme');
    else root.setAttribute('data-theme', settings.theme);
  }, [settings.theme]);

  /* ---- guarded actions -------------------------------------------------- */

  /** Run `action`, asking first when there is written work at stake. */
  const guard = useCallback(
    (request: Omit<ConfirmRequest, 'onConfirm'>, action: () => void, needed = true) => {
      if (!needed || !settings.confirmDestructive) {
        action();
        return;
      }
      setConfirm({ ...request, onConfirm: action });
    },
    [settings.confirmDestructive],
  );

  const requestNewExercise = useCallback(() => {
    guard(
      {
        title: 'Start a new exercise?',
        body: 'This generates a fresh cantus firmus and discards the line you have written.',
        confirmLabel: 'New exercise',
        destructive: true,
      },
      newExercise,
      isDirty(),
    );
  }, [guard, newExercise, isDirty]);

  const requestGrade = useCallback(() => {
    guard(
      {
        title: 'Grade this line?',
        body: 'Every rule violation is revealed at once. Editing a note afterwards retracts the report.',
        confirmLabel: 'Grade it',
      },
      () => {
        gradeNow();
        setSheet('grade');
      },
    );
  }, [guard, gradeNow]);

  /** Used by setup controls that regenerate the exercise. */
  const confirmExerciseSetupChange = useCallback(
    (apply: () => void) => {
      guard(
        {
          title: 'Change the exercise setup?',
          body: 'A new cantus firmus is generated with your choices, and the line you have written is discarded.',
          confirmLabel: 'Change setup',
          destructive: true,
        },
        apply,
        isDirty(),
      );
    },
    [guard, isDirty],
  );

  /* ---- global shortcuts ------------------------------------------------- */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && /^(INPUT|SELECT|TEXTAREA)$/.test(target.tagName)) return;
      // While a sheet is open the shortcuts belong to it, not to the score.
      if (document.querySelector('dialog[open]')) return;

      const meta = e.metaKey || e.ctrlKey;
      const key = e.key.toLowerCase();

      if (meta && key === 'z') {
        e.preventDefault();
        if (e.shiftKey) redo();
        else undo();
        return;
      }
      if (meta && key === 'y') {
        e.preventDefault();
        redo();
        return;
      }
      if (meta) return;

      switch (e.key) {
        case ' ':
          e.preventDefault();
          togglePlay();
          break;
        case '?':

          e.preventDefault();
          setSheet('help');
          break;
        default:
          switch (key) {
            case 'g':
              e.preventDefault();
              requestGrade();
              break;
            case 'n':
              e.preventDefault();
              requestNewExercise();
              break;
            case 't':
              e.preventDefault();
              tap();
              break;
            case ',':
              e.preventDefault();
              setSheet('settings');
              break;
            default:
              return;
          }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [redo, undo, togglePlay, tap, requestGrade, requestNewExercise]);

  /* ---- unlock audio on the first gesture -------------------------------- */
  useEffect(() => {
    const unlock = () => audio.unlock();
    window.addEventListener('pointerdown', unlock, { once: true });
    window.addEventListener('keydown', unlock, { once: true });
    return () => {
      window.removeEventListener('pointerdown', unlock);
      window.removeEventListener('keydown', unlock);
    };
  }, []);

  return (
    <div className="app">
      <Masthead />
      <Toolbar onNewExercise={requestNewExercise} />

      <main className="stage">
        <div className="stage__rail stage__rail--left">
          <IconButton label="Help (?)" onClick={() => setSheet('help')} icon={<HelpIcon />} />
          <IconButton label="Settings" onClick={() => setSheet('settings')} icon={<SettingsIcon />} />
        </div>

        <div className="stage__score">
          <ScoreView onEditKey={() => setSheet('key')} />
          <StatusStrip />
        </div>

        <div className="stage__rail stage__rail--right">
          <IconButton
            label={report ? 'Show the grade again (G)' : 'Grade my work now (G)'}
            className="icon-button--grade"
            onClick={report ? () => setSheet('grade') : requestGrade}
            icon={<GradeArrowIcon />}
          />
        </div>
      </main>

      <p className="exercise-caption">
        {edoOf(exercise)}-EDO · {exercise.key.label} · {settings.profile === 'gran' ? 'Gran' : 'Lim'} rules
      </p>

      <SettingsDialog
        open={sheet === 'settings'}
        onClose={() => setSheet('none')}
        onConfirmNeeded={confirmExerciseSetupChange}
      />
      <HelpDialog open={sheet === 'help'} onClose={() => setSheet('none')} />
      <KeyDialog
        open={sheet === 'key'}
        onClose={() => setSheet('none')}
        onConfirmNeeded={confirmExerciseSetupChange}
      />
      <GradeDialog open={sheet === 'grade'} onClose={() => setSheet('none')} />
      <ConfirmDialog request={confirm} onDismiss={() => setConfirm(null)} />
    </div>
  );
}
